import axios from 'axios';

import {
  useUiResourceContext,
  useConfigContext,
  useResultContext,
} from 'common/components/CopyMagic/contexts';
import { DEV_RESULT_STUB_ACTIVE, DEV_RESULT_STUB } from 'common/components/CopyMagic/constants';
import {
  PromptFormValuesType,
  CopyMagicResultType,
  CopyMagicMetaType,
} from 'common/components/CopyMagic/CopyMagic.types';
import useGeneratorTrackingCallback from 'common/components/CopyMagic/hooks/useGeneratorTrackingCallback';
import copyMagicLog from 'common/components/CopyMagic/shared/copyMagicLog';
import { createClaims, generateResult } from 'common/components/CopyMagic/api';
import getErrorCode from 'common/components/CopyMagic/api/helpers/getErrorCode';
import getErrorMessageFor from 'common/components/CopyMagic/api/helpers/getErrorMessageFor';

type ClaimsResultType = {
  meta?: CopyMagicMetaType;
};

const isModerated = (error: unknown) => getErrorCode(error) === 'FLAGGED_CONTENT';

const useSubmitPromptSetResult = () => {
  const {
    loading,
    promptValues,
    setLoading,
    setError,
    setPromptValues,
    setFeedbackSentiment,
    cancelPendingRequest,
    setRequestSource,
    generateAgain,
    setGenerateAgain,
  } = useUiResourceContext();

  const { setNew, resetResult, result } = useResultContext();
  const isGeneratedAgain = generateAgain;

  const { generatorName } = useConfigContext();
  const trackPromptResponse = useGeneratorTrackingCallback('useTrackPromptResponse');
  const trackFeedbackSentiment = useGeneratorTrackingCallback('useTrackFeedbackSentiment');
  const trackOnGenerate = useGeneratorTrackingCallback('useTrackOnGenerate');

  const submitPrompt = async (values: PromptFormValuesType) => {
    if (loading) {
      return;
    }

    cancelPendingRequest();

    setPromptValues(values);
    setError(null);
    setLoading(true);
    resetResult();
    trackOnGenerate({ promptValues: values });
    trackFeedbackSentiment({
      contentId: result?.id,
      metaData: result?.meta,
    });
    setFeedbackSentiment({ positive: null });

    const requestSource = axios.CancelToken.source();
    setRequestSource(requestSource);
    setGenerateAgain(true);

    let claimsResult: ClaimsResultType = {};
    try {
      let newResult;
      if (DEV_RESULT_STUB_ACTIVE) {
        newResult = DEV_RESULT_STUB[generatorName];
      } else {
        const { token, url, modelName } = await createClaims(generatorName, requestSource);
        claimsResult = {
          meta: {
            modelName,
            isFallbackModel: false,
          },
        };

        newResult = await generateResult<CopyMagicResultType>({
          token,
          url,
          requestData: values,
          requestSource,
        });
      }

      if (newResult) {
        setNew(newResult);
        trackPromptResponse({
          isSuccess: true,
          isModerated: false,
          isGeneratedAgain,
          result: newResult,
          promptValues: values,
        });
      }
      setLoading(false);
    } catch (error) {
      if (axios.isCancel(error)) {
        copyMagicLog('Request cancelled');
      } else {
        setError(getErrorMessageFor(error));
        trackPromptResponse({
          isSuccess: false,
          isModerated: isModerated(error),
          isGeneratedAgain,
          promptValues: values,
          // In case of an error, we still want to track the model name from the claims request
          result: claimsResult,
        });
        copyMagicLog(error, { level: 'error' });
        setLoading(false);
      }
    }
  };

  return {
    initialValues: promptValues,
    submitPrompt,
  };
};

export default useSubmitPromptSetResult;
